import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "@/components/theme-provider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ExternalLink, Hash, Layers, RefreshCw, Shield } from "lucide-react";
import BackButton from "components/navigation/back-button";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { ShowMore } from "components/shared/show-more";
import { StyledMarkdown } from "components/shared/styled-markdown/styled-markdown";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { DatasetDefinitions } from "data/models";
import {
  fetchCatalogueEntry,
  fetchDefinitions,
  fetchUserLicences,
} from "data/queries-common";
import { Product } from "pages/geoscape-data/constants";
import { useEffect, useState } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { CatalogueActionsCard } from "./catalogue-actions-card";
import { CatalogueEntryCaseStudies } from "./catalogue-entry-case-studies";
import { CatalogueEntryDataDictionary } from "./catalogue-entry-data-dictionary";
import { CatalogueEntryCoverage } from "./catalogue-entry-coverage";
import { CatalogueEntryMetadataCard } from "./catalogue-entry-metadata-card";

// Reverse mapping for NATIONAL X back to backend keys
const REVERSE_PRODUCT_MAPPING: Record<string, string> = {
  "NATIONAL PLANNING": "GEOSCAPE PLANNING",
  "NATIONAL PLANNING INSIGHTS": "GEOSCAPE PLANNING INSIGHTS",
  "NATIONAL BUILDINGS": "GEOSCAPE BUILDINGS",
  "NATIONAL CADASTRE": "GEOSCAPE CADASTRE",
  "NATIONAL PROPERTY": "GEOSCAPE PROPERTY",
  "NATIONAL TREES": "GEOSCAPE TREES",
  "NATIONAL ROADS": "GEOSCAPE ROADS",
  "NATIONAL AIRPORTS": "GEOSCAPE AIRPORTS",
  "NATIONAL RAIL": "GEOSCAPE RAIL",
  "NATIONAL LOCALITIES": "GEOSCAPE LOCALITIES",
  "NATIONAL LOCAL GOVERNMENT AREAS": "GEOSCAPE LOCAL GOVERNMENT AREAS",
  "NATIONAL WARDS": "GEOSCAPE WARDS",
  "NATIONAL SOLAR": "GEOSCAPE SOLAR",
  "NATIONAL SURFACE COVER": "GEOSCAPE SURFACE COVER",
  "NATIONAL GREENSPACE": "GEOSCAPE GREENSPACE",
  "NATIONAL HYDROLOGY": "GEOSCAPE HYDROLOGY",
  "NATIONAL ELECTORAL BOUNDARIES": "ELECTORAL BOUNDARIES",
  "NATIONAL POSTCODE BOUNDARIES": "POSTCODE BOUNDARIES",
};

const StatChip = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5">
    <Icon className="size-4 text-muted-foreground" />
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

export const CatalogueEntry = () => {
  let { productName }: any = useParams();
  const [identityState] = useIdentity();

  const [licenced, setLicenced] = useState<boolean>(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const catalogueEntryQuery = useQuery({
    queryKey: [QueryKeys.catalogueEntryKey, productName],
    queryFn: () => fetchCatalogueEntry(productName),
  });

  const userLicences = useQuery({
    queryKey: [QueryKeys.userLicences, productName],
    queryFn: () => fetchUserLicences(),
  });

  const datasetDefinitions: UseQueryResult<DatasetDefinitions> = useQuery({
    queryKey: [QueryKeys.definitionsKey],
    queryFn: () => fetchDefinitions(),
  });

  const isLoading: Boolean =
    catalogueEntryQuery.isPending ||
    datasetDefinitions.isPending ||
    userLicences.isPending;
  const isError: Boolean =
    catalogueEntryQuery.isError ||
    datasetDefinitions.isError ||
    userLicences.isError;
  const isSuccess: Boolean =
    catalogueEntryQuery.isSuccess &&
    datasetDefinitions.isSuccess &&
    userLicences.isSuccess;

  useEffect(() => {
    // Reverse map NATIONAL X back to backend key for licence check
    const backendProductName =
      REVERSE_PRODUCT_MAPPING[productName] || productName;

    const isProductLicensed = userLicences.data?.dataset_products.some(
      (product: any) => {
        return product.product_name === backendProductName;
      },
    );

    if (isProductLicensed) {
      setLicenced(true);
    }
  }, [isSuccess]);

  var datasetId = "";
  if (isSuccess) {
    // Reverse map NATIONAL X back to backend key for metadata lookup
    let backendId =
      REVERSE_PRODUCT_MAPPING[catalogueEntryQuery.data.id] ||
      catalogueEntryQuery.data.id;

    datasetId = (
      backendId === "GEOSCAPE PLANNING INSIGHTS"
        ? "GEOSCAPE PLANNING INSIGHTS - BASE"
        : backendId
    ) as Product;
  }

  return (
    <div className="relative min-w-0 space-y-4">
      <div className="flex justify-end gap-4">
        <BackButton />
      </div>

      <ReactQueryErrorWrapper
        queries={[catalogueEntryQuery, userLicences, datasetDefinitions]}
        mutations={[]}
      />

      {isLoading && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Skeleton className="h-125 w-full" />
          </div>
          <div>
            <Skeleton className="h-125 w-full" />
          </div>
        </div>
      )}

      {isSuccess && (
        <main className="grid gap-4 lg:grid-cols-3">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-4 min-w-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-semibold tracking-tight">
                  {catalogueEntryQuery.data.displayProduct}
                </CardTitle>
                {catalogueEntryQuery.data.subtitle && (
                  <CardDescription>
                    {catalogueEntryQuery.data.subtitle}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2 text-xs">
                  {catalogueEntryQuery.data.metadata.dataType?.length > 0 && (
                    <StatChip
                      icon={Layers}
                      label="Type"
                      value={catalogueEntryQuery.data.metadata.dataType.join(
                        ", ",
                      )}
                    />
                  )}
                  <StatChip
                    icon={Hash}
                    label="Features"
                    value={catalogueEntryQuery.data.metadata.totalFeatureCount.toLocaleString()}
                  />
                  <StatChip
                    icon={RefreshCw}
                    label="Updated"
                    value={catalogueEntryQuery.data.metadata.updateFrequency}
                  />
                  <StatChip
                    icon={Shield}
                    label="Licence"
                    value={catalogueEntryQuery.data.metadata.license}
                  />
                </div>

                <ShowMore>
                  <div className="text-sm text-muted-foreground prose max-w-none [&_p:first-child]:mt-0 [&_p:last-child]:mb-[0.625em]">
                    <StyledMarkdown>
                      {catalogueEntryQuery.data.description}
                    </StyledMarkdown>
                  </div>
                </ShowMore>

                {(catalogueEntryQuery.data.productDescriptionLink ||
                  catalogueEntryQuery.data.latestReleaseReportLink ||
                  catalogueEntryQuery.data.metadata.licenseLink) && (
                  <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-4 text-sm">
                    {catalogueEntryQuery.data.productDescriptionLink && (
                      <a
                        href={catalogueEntryQuery.data.productDescriptionLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                      >
                        Product Description
                        <ExternalLink className="size-3.5 shrink-0" />
                      </a>
                    )}
                    {catalogueEntryQuery.data.latestReleaseReportLink && (
                      <a
                        href={catalogueEntryQuery.data.latestReleaseReportLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                      >
                        Latest Release Report
                        <ExternalLink className="size-3.5 shrink-0" />
                      </a>
                    )}
                    {catalogueEntryQuery.data.metadata.licenseLink && (
                      <a
                        href={catalogueEntryQuery.data.metadata.licenseLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                      >
                        Licence Terms
                        <ExternalLink className="size-3.5 shrink-0" />
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <CatalogueEntryCaseStudies
              caseStudies={catalogueEntryQuery.data.caseStudies}
            />
            <CatalogueEntryDataDictionary
              dataDictionaries={catalogueEntryQuery.data.dataDictionaries}
            />
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-4 lg:max-w-100 ">
            <CatalogueActionsCard
              licenced={licenced}
              displayProduct={productName}
            />
            <CatalogueEntryCoverage
              datasetId={datasetId}
              image={`data:image/png;base64,${
                isDark
                  ? catalogueEntryQuery.data.imageDark
                  : catalogueEntryQuery.data.imageLight
              }`}
            />
            {datasetDefinitions.data?.metadata[datasetId] && (
              <CatalogueEntryMetadataCard
                metadata={catalogueEntryQuery.data.metadata}
                definitionsMetadata={
                  datasetDefinitions.data.metadata[datasetId]
                }
              />
            )}
          </div>
        </main>
      )}
    </div>
  );
};
